import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { CEREMONY } from "@/constants/testIds";
import { HOUSES, houseFor } from "@/lib/houses";
import NarratedText from "@/components/NarratedText";
import { playAudioLogo } from "@/lib/audioLogo";
import { useNarrator } from "@/lib/narrator";

export default function DeckReveal() {
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [status, setStatus] = useState("loading");
  const [flippedIdx, setFlippedIdx] = useState(-1); // last card flipped
  const pollRef = useRef(null);
  const { muted } = useNarrator();

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/deck/status");
        if (!alive) return;
        if (data.status === "ready") {
          setDeck(data);
          setStatus("ready");
          clearInterval(pollRef.current);
        } else if (data.status === "failed") {
          setStatus("failed");
          clearInterval(pollRef.current);
        } else if (data.status === "none") {
          await api.post("/deck/generate").catch(() => {});
          setStatus("pending");
        } else {
          setStatus("pending");
        }
      } catch (e) {
        setStatus("failed");
        clearInterval(pollRef.current);
      }
    };
    poll();
    pollRef.current = setInterval(poll, 3500);
    return () => {
      alive = false;
      clearInterval(pollRef.current);
    };
  }, []);

  // Auto-flip cards one at a time after all backs have arrived
  useEffect(() => {
    if (status !== "ready" || !deck) return;
    const timeouts = [];
    // Wait for entry animations to complete, then flip each card 1.3s apart
    const startDelay = 2400 + deck.cards.length * 260;
    deck.cards.forEach((_, i) => {
      timeouts.push(
        setTimeout(() => {
          setFlippedIdx(i);
          playAudioLogo({ muted });
        }, startDelay + i * 1300),
      );
    });
    return () => timeouts.forEach(clearTimeout);
  }, [status, deck, muted]);

  const element = deck?.element || "Fire";
  const house = houseFor(element);

  return (
    <div
      data-testid={CEREMONY.deckContainer}
      className="relative min-h-screen bg-[#080808] flex items-center justify-center py-16"
    >
      <div className="relative z-10 w-full max-w-6xl px-6 sm:px-10 text-center">
        <div className="font-overline mb-6 text-[#C8A96E]">First-time Deck Reveal</div>

        {status !== "ready" && (
          <div className="mt-24">
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.6 }}
              className="font-heading text-4xl sm:text-5xl text-[#F4EFE6] font-light mb-8"
            >
              {status === "failed" ? "The deck refused to render." : "Your deck is being drawn."}
            </motion.h1>
            {status === "pending" && (
              <>
                <div className="font-heading text-xl text-[#F4EFE6]/60 breathe">
                  This takes a moment — nothing here is rushed.
                </div>
                <div className="mt-12 flex justify-center gap-6">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <motion.div
                      key={i}
                      className="w-16 aspect-square rounded-lg card-frame"
                      animate={{ opacity: [0.25, 0.85, 0.25] }}
                      transition={{ duration: 3, repeat: Infinity, delay: i * 0.35, ease: "easeInOut" }}
                    />
                  ))}
                </div>
              </>
            )}
            {status === "failed" && (
              <button className="btn-sacred mt-10" onClick={() => window.location.reload()}>
                Try again
              </button>
            )}
          </div>
        )}

        {status === "ready" && deck && (
          <>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.6 }}
              className="font-heading text-4xl sm:text-5xl text-[#F4EFE6] font-light"
            >
              Your {element} House Deck.
            </motion.h1>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.4, delay: 1 }}
              className="mt-4 max-w-2xl mx-auto"
            >
              <NarratedText
                id={`deck-reveal-descriptor-${element}`}
                text={`Your ${element} House Deck. ${house.descriptor}`}
                className="font-heading text-lg text-[#F4EFE6]/60"
                delay={1400}
              />
            </motion.div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-10">
              {deck.cards.map((c, i) => {
                const isFlipped = flippedIdx >= i;
                return (
                  <motion.div
                    key={c.index}
                    data-testid={CEREMONY.deckCard(c.index)}
                    initial={{ opacity: 0, y: 24, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 1.4,
                      delay: 1.8 + i * 0.4,
                      ease: [0.25, 0.6, 0.3, 1],
                    }}
                    className="flex flex-col items-center"
                    style={{ perspective: "1600px" }}
                  >
                    <motion.div
                      animate={{ rotateY: isFlipped ? 180 : 0 }}
                      transition={{ duration: 1.4, ease: [0.25, 0.6, 0.3, 1] }}
                      className="relative w-full aspect-square rounded-2xl"
                      style={{
                        transformStyle: "preserve-3d",
                        boxShadow: "0 22px 60px rgba(0,0,0,0.55)",
                        willChange: "transform",
                        WebkitBackfaceVisibility: "hidden",
                        backfaceVisibility: "hidden",
                      }}
                    >
                      {/* Back */}
                      <div
                        className="absolute inset-0 rounded-2xl overflow-hidden"
                        style={{ backfaceVisibility: "hidden" }}
                      >
                        <img
                          src={house.back}
                          alt={`${element} House card back`}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      </div>
                      {/* Face */}
                      <div
                        className="absolute inset-0 rounded-2xl overflow-hidden bg-black"
                        style={{
                          transform: "rotateY(180deg)",
                          backfaceVisibility: "hidden",
                        }}
                      >
                        <img
                          src={mediaUrl(c.image_url)}
                          alt={c.title}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      </div>
                    </motion.div>
                    <div
                      className="mt-4 font-heading italic text-base sm:text-lg text-[#F4EFE6] transition-opacity duration-700"
                      style={{ opacity: isFlipped ? 1 : 0 }}
                    >
                      {c.title}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.4, delay: 3 + deck.cards.length * 1.3 }}
              className="mt-20"
            >
              <button
                data-testid={CEREMONY.deckContinueBtn}
                onClick={() => navigate("/app/oracle")}
                className="btn-sacred btn-sacred-solid"
              >
                Enter the Oracle
              </button>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
